import { DeleteIcon } from "@chakra-ui/icons";
import {
  Box,
  Divider,
  HStack,
  VStack,
  Text,
  BoxProps,
  MenuList,
  Menu,
  MenuButton,
  MenuItem,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import { IntegerField } from "../fields/IntegerField/IntegerField";
import { TextField } from "../fields/TextField/TextField";
import { Button, ButtonGroup, IconButton } from "@chakra-ui/button";
import {
  EllipsisIcon,
  PlusIcon,
  ReplaceAllIcon,
  ValidateIcon,
} from "../../styles/icons";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem } from "../Dnd/SortableItem";
import { getIn, useFormikContext } from "formik";
import { ErrorMessage } from "../ErrorMessage/ErrorMessage";

interface KeyValuePairFieldProps {
  headerLabel: string;
  fieldName: string;
  keyValuePairs: { key: string; value: unknown }[] | null | undefined;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  useNumericFieldKey?: boolean;
  useNumericFieldValue?: boolean;
}

export const KeyValuePairField = ({
  headerLabel,
  fieldName,
  keyValuePairs,
  setFieldValue,
  useNumericFieldKey = false,
  useNumericFieldValue = false,
  ...props
}: KeyValuePairFieldProps & BoxProps) => {
  const dep = JSON.stringify(keyValuePairs);

  const view = useMemo(() => {
    return (
      <Box
        w="100%"
        p={4}
        border="1px solid"
        borderColor="cherryButtonDark.50"
        borderRadius="md"
        {...props}
      >
        <HStack w="100%">
          <Text flexGrow={2} fontSize="sm">
            <b>{headerLabel}</b> :
          </Text>
          <Button
            size="xs"
            leftIcon={<PlusIcon />}
            onClick={() => {
              if (keyValuePairs) {
                keyValuePairs?.push({ key: "", value: "" });
                setFieldValue(fieldName, keyValuePairs);
              } else {
                setFieldValue(fieldName, [{ key: "", value: "" }]);
              }
            }}
            colorScheme="cherryButton"
          >
            New {headerLabel}
          </Button>
        </HStack>

        <Divider mb={2} mt={1} />
        <VStack w="100%">
          {(keyValuePairs?.length &&
            keyValuePairs?.map((kvp, kvpIdx) => {
              return (
                <HStack w="100%" key={kvpIdx}>
                  {(useNumericFieldKey && (
                    <IntegerField
                      label={`Key ${kvpIdx + 1}`}
                      name={`${fieldName}[${kvpIdx}].key`}
                    />
                  )) || (
                    <TextField
                      label={`Key ${kvpIdx + 1}`}
                      name={`${fieldName}[${kvpIdx}].key`}
                    />
                  )}
                  {(useNumericFieldValue && (
                    <IntegerField
                      label={`Value ${kvpIdx + 1}`}
                      name={`${fieldName}[${kvpIdx}].value`}
                    />
                  )) || (
                    <TextField
                      label={`Value ${kvpIdx + 1}`}
                      name={`${fieldName}[${kvpIdx}].value`}
                    />
                  )}

                  <Box h="100%" pb={1} alignSelf="flex-end">
                    <IconButton
                      aria-label={`Delete ${headerLabel}.`}
                      title={`Delete ${headerLabel}.`}
                      icon={<DeleteIcon />}
                      size="sm"
                      onClick={() => {
                        keyValuePairs?.splice(kvpIdx, 1);
                        setFieldValue(fieldName, keyValuePairs);
                      }}
                    />
                  </Box>
                </HStack>
              );
            })) || (
            <Text fontStyle="italic" fontSize="sm">
              {headerLabel} has not been set.
            </Text>
          )}
        </VStack>
      </Box>
    );
  }, [dep]);

  return <>{view}</>;
};
